import React from 'react';
import {List, Set} from 'immutable';
import './SessionAgendaStep.styl';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import RadioField from 'components/pure/utils/form/RadioField';
import SelectField from 'components/pure/utils/form/SelectField';
import {ColorDot} from 'components/utils/ColorDot';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import {ELLIPSIS_DELAY} from 'components/StatsPage2.utils';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';

export default class ReviewTypeStep extends React.Component {
  constructor(props) {
    super(props);
    this.onDisassociateReviewType = this.onDisassociateReviewType.bind(this);
    this.setUseGenericQuestions = this.setUseGenericQuestions.bind(this);
  }

  setUseGenericQuestions(useGenericQuestions) {
    const {model, onChange} = this.props;
    let changed = model.set('useGenericQuestions', useGenericQuestions);
    // can't have both generic and scorecards
    if (useGenericQuestions) {
      changed = changed.set('scorecardId', null);
    }
    onChange(changed);
  }

  onDisassociateReviewType(type) {
    const {model, onChange} = this.props;

    onChange(
      model.set(
        'reviewTypeIds',
        model.reviewTypeIds.filter(id => id !== type.id)
      )
    );
  }

  render() {
    const {types, model, onChange, showErrors, scorecards, validate, frozen} = this.props;
    const useGenericQuestions = model.useGenericQuestions;
    const isSkillBased = model.contentDetails.some(detail => detail.isSkillBased);
    const scorecard = isSkillBased ? scorecards.find(sc => sc.id === model.scorecardId) : null;
    const skillIds = model.contentDetails.map(d => d.skillId);
    const skillNames = model.contentDetails.map(d => d.skillName);
    const questions = useGenericQuestions
      ? model.contentDetails.map(skill => ({
          id: skill.skillId,
          text: `How would you rate ${
            model && model.recipient && model.recipient.info
              ? model.recipient.info.firstName
              : ' the rep '
          } on ${skill.skillName} in this call?`,
        }))
      : scorecard
      ? scorecard.questions.toList().filter(q => skillIds.toSet().intersect(q.skillIds).size > 0)
      : List();
    const errors = validate(model);

    const typeIds = types.keySeq().toJS();

    const anyTypeIncludesCalls =
      model.reviewTypeIds.filter(reviewTypeId => types.get(reviewTypeId).includesCalls).size > 0;

    const scorecardOptions = scorecards.toList().map(sc => ({
      label: sc.name,
      value: sc.id,
    }));

    return (
      <div className="coaching-step session-agenda-step">
        <p>What kind of meeting types should be applicable to this plan?</p>
        <div className="multi-select-container">
          <FieldContainer
            error={showErrors ? errors.reviewType : null}
            className={frozen && 'disabled'}>
            <Autocomplete
              multiple
              fullWidth
              autoHighlight
              filterSelectedOptions
              renderInput={params => (
                <TextField {...params} size="small" variant="outlined" disabled={frozen} />
              )}
              options={typeIds}
              getOptionLabel={id => types.get(id).name}
              onChange={(e, types) => onChange(model.set('reviewTypeIds', Set(types)))}
              value={model.reviewTypeIds.toJS()}
              renderOption={id => <PrettyDisplay type={types.get(id)} />}
              renderTags={(ids, getTagProps) =>
                ids.map(id => (
                  <PrettyValueDisplay key={id} type={types.get(id)} tagProps={getTagProps(id)} />
                ))
              }
            />
          </FieldContainer>
          <div>
            <a
              className="generic-link"
              href="https://support.mediafly.com/hc/en-us/articles/7682590282011-Build-Coaching-Meeting-Types"
              target="_blank"
              rel="noreferrer">
              Learn about meeting types
            </a>
          </div>
        </div>
        {isSkillBased && !(scorecards && scorecards.size) && (
          <div>
            This plan will use generic skill questions, because there are no scorecards configured
            for {model.recipient.info.firstName}'s department that have questions related to{' '}
            {skillNames.join(' & ')}
          </div>
        )}
        {isSkillBased && anyTypeIncludesCalls && scorecards && scorecards.size > 0 && (
          <div>
            <p>For call questions, do you want to:</p>
            <RadioField
              label="Present skill questions from an applicable scorecard?"
              disabled={frozen}
              checked={!useGenericQuestions}
              onClick={() =>
                onChange(
                  model.set('scorecardId', scorecards.first().id).set('useGenericQuestions', false)
                )
              }
            />
            {!useGenericQuestions && (
              <FieldContainer
                error={showErrors ? errors.scorecardId : null}
                className="scorecard-select"
                labelType="div">
                <SelectField
                  disabled={frozen}
                  removeSelected
                  options={scorecardOptions}
                  onChange={scorecardId =>
                    onChange(
                      model.set('scorecardId', scorecardId).set('useGenericQuestions', false)
                    )
                  }
                  value={model.scorecardId}
                />
              </FieldContainer>
            )}
            <RadioField
              label="Present a generic skill question?"
              disabled={frozen}
              checked={useGenericQuestions}
              onClick={() => this.setUseGenericQuestions(true)}
            />
            <hr className="preview-hr" />
            {questions.size > 0 && <div className="preview-heading">Preview questions:</div>}
            {questions.size > 0 && (
              <FieldContainer className={frozen && 'disabled'}>
                <ol className="preview">
                  {questions.map(question => (
                    <li key={question.id}>{question.text}</li>
                  ))}
                </ol>
              </FieldContainer>
            )}
          </div>
        )}
      </div>
    );
  }
}

const PrettyDisplay = ({type}) => (
  <div className="coaching-plan-wizard-review-type-item">
    <GenericTooltip
      hoverBehavior
      delay={ELLIPSIS_DELAY}
      positionTop
      tooltip={type.meetingCategory.name}>
      <ColorDot color={type.meetingCategory.color} size={13} />
    </GenericTooltip>
    <span>{type.name}</span>
  </div>
);

const PrettyValueDisplay = ({type, tagProps}) => (
  <Chip
    size="small"
    variant="outlined"
    key={type.id}
    label={<PrettyDisplay type={type} />}
    {...tagProps}
  />
);
